import { useEffect, useRef } from "react";

/**
 * Scroll-in reveal, ported from the marketing site's shared behavior —
 * site/plaintosee-unified/public/js/site.js (bottom section, grep
 * "reveal-init"/"is-revealed") and public/css/site.css (same grep, ~line
 * 216+). Same timing/offsets as the donor so the app and marketing surfaces
 * read as one product (Rich, 2026-07-04).
 *
 * The class is added FROM JS, so a hook that never runs (old browser, test
 * environment, reduced-motion) leaves the element with no `reveal-init`
 * class at all — plainly visible, never hidden. That's the donor's safety
 * property and this preserves it: no IntersectionObserver -> no-op; jsdom
 * has no IntersectionObserver, so this is also a no-op in tests without any
 * special test-only branching.
 *
 * Attach the returned ref to the content block that should fade/slide in —
 * one hook call per block (do NOT call this on the hero or any
 * above-the-fold action panel; people act there and must never wait on it).
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce =
      typeof window.matchMedia !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) return;

    el.classList.add("reveal-init");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px 0px 0px", threshold: 0.08 },
    );
    io.observe(el);

    // Bottom dead-zone sweep, same as the donor: the -12%-of-viewport
    // effective bottom margin (rootMargin 0 + threshold 0.08 acting near the
    // edge) means a short trailing block can sit in the last stretch of
    // scroll and never cross the trigger line. Once the page is scrolled to
    // its bottom, reveal whatever's left so nothing stays stuck faded.
    function sweepBottom() {
      if (
        !el ||
        window.innerHeight + window.scrollY <
          document.documentElement.scrollHeight - 2
      ) {
        return;
      }
      el.classList.add("is-revealed");
      window.removeEventListener("scroll", sweepBottom);
    }
    window.addEventListener("scroll", sweepBottom, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", sweepBottom);
    };
  }, []);

  return ref;
}
